import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';

import {
  Input,
  CloseButton,
  BaseButton,
  CustomDatePicker,
  VisaTypeList,
  useT,
} from '@repo/common';

import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';
import { ReactComponent as CalendarIcon } from '@repo/common/assets/icons/16px/icon_16px_calendar_outline.svg';

// "No Visa" 처럼 만료일이 필요 없는 자격
const isNoVisa = (label) => typeof label === 'string' && label.startsWith('No');

// entry(visaType=code, visaTypeText=text) → 편집용 풀 라벨
const toLabel = (entry) =>
  entry?.visaType ? `${entry.visaType} ${entry.visaTypeText ?? ''}`.trim() : '';

// 풀 라벨 → 저장용 code / text 분리 (기존 desktop 저장 형식과 동일)
const splitLabel = (label) => {
  if (!label) return { visaType: null, visaTypeText: null };
  const [code, ...rest] = label.split(' ');
  return { visaType: code, visaTypeText: rest.join(' ') || null };
};

const toDate = (d) => {
  if (!d) return null;
  const parsed = d instanceof Date ? d : new Date(d);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const VisaInfoFullPage = ({ initialData, onClose, onSave }) => {
  const t = useT();
  const [visaLabel, setVisaLabel] = useState(() => toLabel(initialData));
  const [expiryDate, setExpiryDate] = useState(() =>
    toDate(initialData?.expiryDate),
  );

  const [openVisaType, setOpenVisaType] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);

  const visaWrapRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => {
    if (!openVisaType) return;
    const handleClickOutside = (e) => {
      if (visaWrapRef.current && !visaWrapRef.current.contains(e.target)) {
        setOpenVisaType(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openVisaType]);

  const expiryDisabled = isNoVisa(visaLabel);
  const canAdd = !!visaLabel && (expiryDisabled || !!expiryDate);

  const handleAdd = () => {
    const { visaType, visaTypeText } = splitLabel(visaLabel);
    onSave({
      visaType,
      visaTypeText,
      expiryDate: expiryDisabled ? null : expiryDate,
    });
  };

  return (
    <div className='fixed inset-0 z-[60] flex flex-col bg-grayscale-gray00'>
      {/* Header */}
      <div className='relative shrink-0 h-[54px] px-4 py-3 flex items-center justify-between border-b border-grayscale-gray02'>
        <CloseButton onClick={onClose} />
        <span className='typo-b16Strong text-text-primary'>
          {t('Visa info', '비자 정보')}
        </span>
        <div className='size-6' />
      </div>

      {/* Body */}
      <div className='flex-1 overflow-y-auto px-4 pt-[30px] pb-[160px] flex flex-col gap-4'>
        {/* Current Visa type */}
        <div ref={visaWrapRef} className='relative'>
          <div onClick={() => setOpenVisaType((v) => !v)}>
            <Input
              label={t('Current Visa type', '현재 비자 종류')}
              placeholder={t(
                'Select your visa type',
                '비자 종류를 선택해 주세요',
              )}
              value={visaLabel}
              readOnly
              hideCount
              rightIcon={openVisaType ? <ArrowTopIcon /> : <ArrowBottomIcon />}
            />
          </div>
          {openVisaType && (
            <div className='relative w-full mt-1.5 z-10'>
              <VisaTypeList
                selectedOption={visaLabel}
                onSelect={(value) => {
                  setVisaLabel(value);
                  if (isNoVisa(value)) setExpiryDate(null);
                  setOpenVisaType(false);
                }}
                isMyPagePersonalInfo={true}
              />
            </div>
          )}
        </div>

        {/* Visa expiry date */}
        <div className='relative'>
          <div onClick={() => !expiryDisabled && setOpenDatePicker(true)}>
            <Input
              label={t('Visa expiry date', '비자 만료일')}
              placeholder={t('Select expiry date', '만료일을 선택해 주세요')}
              value={expiryDate ? format(expiryDate, 'dd MMM yyyy') : ''}
              readOnly
              hideCount
              disabled={expiryDisabled}
              rightIcon={<CalendarIcon />}
            />
          </div>
          {openDatePicker && (
            <CustomDatePicker
              isVisaExpiryDate
              selectedDate={expiryDate}
              onChange={(date) => {
                setExpiryDate(date);
                setOpenDatePicker(false);
              }}
              $isMobile={true}
              onClose={() => setOpenDatePicker(false)}
              isMyPagePersonalInfoVisa
            />
          )}
        </div>
      </div>

      <div className='shrink-0 p-4 border-t border-grayscale-gray02'>
        <BaseButton
          onClick={handleAdd}
          size='l'
          hideIcon
          width='100%'
          disabled={!canAdd}
        >
          {t('Add', '추가')}
        </BaseButton>
      </div>
    </div>
  );
};

export default VisaInfoFullPage;
